import { Card, Button, Alert, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useRequest } from "../hooks/useRequest";
import { useAuth } from "../context/AuthContext";
import { useLeagueMembership } from "../context/LeagueMembershipContext";
import { useLeague } from "../hooks/useLeague";
import { getSeasonsUrl } from "../config/api";
import { Season } from "../types/models";
import RecentGames from "./RecentGames";
import UpcomingGames from "./UpcomingGames";
import SeasonStanding from "./SeasonStanding";
import { PaginatedResponse } from "../types/api";

function AdminDashboard() {
  const auth = useAuth();
  const { currentLeague, setSelectedSeasonId } = useLeague();
  const { canManageLeague, isLoading: isMembershipLoading } =
    useLeagueMembership();

  const seasonsUrl = currentLeague
    ? getSeasonsUrl(currentLeague.id)
    : undefined;

  const { data: seasonsResponse, error } = useRequest<
    PaginatedResponse<Season>
  >(seasonsUrl, auth.token);

  const seasons = seasonsResponse?.data;
  const hasSeasons = seasons && seasons.length > 0;
  const activeSeasonId = currentLeague?.active_season_id;
  const hasActiveSeason = hasSeasons && activeSeasonId !== null;
  const activeSeason = seasons?.find((s) => s.id === activeSeasonId);

  return (
    <div className="mt-4">
      <h2 className="mb-4">Dashboard</h2>

      {currentLeague && (
        <Alert variant="info" className="d-flex align-items-center gap-4 mb-3">
          <div>
            <small className="text-muted d-block mb-1">Managing League</small>
            <strong>{currentLeague.name}</strong> ({currentLeague.abbr})
          </div>
          {activeSeason && (
            <div className="border-start ps-4 d-flex align-items-center gap-3">
              <div>
                <small className="text-muted d-block mb-1">Active Season</small>
                <strong>{activeSeason.name}</strong>
              </div>
              <Link
                to={`/admin/seasons/${activeSeason.id}/overview`}
                onClick={() => setSelectedSeasonId(activeSeason.id)}
              >
                <Button variant="outline-primary" size="sm">
                  {canManageLeague ? "Manage" : "View"}
                </Button>
              </Link>
            </div>
          )}
        </Alert>
      )}

      {!hasSeasons && !error && seasons && canManageLeague && (
        <Card className="text-center">
          <Card.Body>
            <Card.Title>Get Started</Card.Title>
            <Card.Text className="text-muted mb-4">
              You haven't created any seasons yet. Create your first season to
              start managing games and teams.
            </Card.Text>
            <Link to="/setup/season">
              <Button variant="primary" size="lg">
                Create Your First Season
              </Button>
            </Link>
          </Card.Body>
        </Card>
      )}

      {hasSeasons && !hasActiveSeason && canManageLeague && (
        <Card className="text-center">
          <Card.Body>
            <Card.Title>Set an Active Season</Card.Title>
            <Card.Text className="text-muted mb-4">
              You have created seasons, but no season is currently set as
              active. An active season is required for the public-facing league
              pages to display current standings, schedules, and stats.
            </Card.Text>
            <Link to="/admin/league-settings">
              <Button variant="primary" size="lg">
                Go to League Settings
              </Button>
            </Link>
            <Card.Text className="text-muted mt-3">
              <small>
                In League Settings, you can select which season should be active
                and visible to the public.
              </small>
            </Card.Text>
          </Card.Body>
        </Card>
      )}

      {seasons &&
        !hasActiveSeason &&
        !canManageLeague &&
        !isMembershipLoading && (
          <Card className="text-center">
            <Card.Body>
              <Card.Title>No Active Season</Card.Title>
              <Card.Text className="text-muted">
                This league doesn't have an active season yet. A league owner or
                admin needs to set one up before games can be scored here.
              </Card.Text>
            </Card.Body>
          </Card>
        )}

      {hasActiveSeason && activeSeasonId && (
        <Row className="g-3">
          <Col md={6}>
            <Card>
              <Card.Header>
                <Card.Title className="mb-0">Recent Games</Card.Title>
              </Card.Header>
              <Card.Body>
                <RecentGames seasonId={activeSeasonId} limit={5} />
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <Card.Header>
                <Card.Title className="mb-0">Upcoming Games</Card.Title>
              </Card.Header>
              <Card.Body>
                <UpcomingGames seasonId={activeSeasonId} limit={5} />
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12}>
            <SeasonStanding seasonId={activeSeasonId} title="Standings" />
          </Col>
        </Row>
      )}
    </div>
  );
}

export default AdminDashboard;
